import { useMemo, useState } from 'react';
import { Button, ConfigProvider, Form, Input } from 'antd';
import { ArrowLeft, ArrowRight, CreditCard, GitCompareArrows, Globe, Lock, Mail, Repeat, ShieldCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BarahWordmark, ProductBrand } from '@barahpay/shared';
import { useAuth } from '../auth/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { ApiErrorAlert } from '../components/Feedback';
import { buildTheme } from '../theme';

interface FormValues {
  email: string;
  password: string;
  mfaCode?: string;
}

/** The capabilities the platform actually ships, as the brand panel's feature pills. */
const FEATURES: { key: string; icon: LucideIcon }[] = [
  { key: 'featurePayments', icon: CreditCard },
  { key: 'featureSubscriptions', icon: Repeat },
  { key: 'featureReconciliation', icon: GitCompareArrows },
  { key: 'featureAudit', icon: ShieldCheck },
];

const STATS = ['Scope', 'Languages', 'Monitoring'] as const;

/**
 * The Barah Plus sign-in screen: a navy half carrying the centred product lockup, the headline, the
 * feature pills and the stats, and a white half carrying the form. The form half is light in both
 * themes, as in Barah Plus, so it renders under the light antd theme whatever the portal is set to.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { signIn, mfaRequired } = useAuth();
  const { language, rtl, toggleLanguage } = useLanguage();
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const lightTheme = useMemo(() => buildTheme('light'), []);

  async function onSubmit(values: FormValues) {
    setSubmitting(true);
    setError(null);

    try {
      await signIn(values.email, values.password, values.mfaCode || undefined);
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  // The arrow points the way the reader is going, so it flips with the direction.
  const SubmitArrow = rtl ? ArrowLeft : ArrowRight;

  return (
    <div className="bp-login">
      <aside className="bp-login__panel">
        <div className="bp-login__glow" />
        <div className="bp-login__glow bp-login__glow--low" />
        <div className="bp-login__dots" />
        <div className="bp-login__hairline" />

        <div className="bp-login__content">
          <div className="bp-login__hero">
            <div className="bp-login__hero-brand">
              <ProductBrand size="lg" orientation="vertical" onDark />
            </div>

            <h2 className="bp-login__headline">{t('auth.headline')}</h2>
            <p className="bp-login__lede">{t('auth.lede')}</p>

            <div className="bp-login__pills">
              {FEATURES.map(({ key, icon: Icon }) => (
                <span key={key} className="bp-login__pill">
                  <Icon aria-hidden />
                  {t(`auth.${key}`)}
                </span>
              ))}
            </div>

            <div className="bp-login__stats">
              {STATS.map((stat) => (
                <div key={stat}>
                  <span className="bp-login__stat-value">{t(`auth.stat${stat}Value`)}</span>
                  <span className="bp-login__stat-label">{t(`auth.stat${stat}Label`)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </aside>

      <main className="bp-login__form bp-force-light">
        <button type="button" className="bp-login__language" onClick={toggleLanguage}>
          <Globe size={16} aria-hidden />
          <span>{language === 'ar' ? 'English' : 'العربية'}</span>
        </button>

        <div className="bp-login__blobs" aria-hidden>
          <div className="bp-login__blob bp-login__blob--a" />
          <div className="bp-login__blob bp-login__blob--b" />
        </div>

        <ConfigProvider theme={lightTheme}>
          <div className="bp-login__body">
            {/* Below the split-screen breakpoint the navy panel is hidden, so the lockup moves here. */}
            <div className="bp-login__compact-brand">
              <ProductBrand size="md" />
            </div>

            <div>
              <h1 className="bp-login__title">{t('auth.welcome')}</h1>
              <p className="bp-login__subtitle">{t('auth.signInSubtitle')}</p>
            </div>

            <div>
              <ApiErrorAlert error={error} />
            </div>

            <Form<FormValues> layout="vertical" onFinish={onSubmit} requiredMark={false} autoComplete="on" size="large">
              <Form.Item
                name="email"
                label={t('auth.email')}
                rules={[{ required: true, message: t('common.required') }, { type: 'email' }]}
              >
                <Input prefix={<Mail aria-hidden />} placeholder="admin@barah.sa" autoComplete="username" autoFocus />
              </Form.Item>

              <Form.Item
                name="password"
                label={t('auth.password')}
                rules={[{ required: true, message: t('common.required') }]}
              >
                <Input.Password prefix={<Lock aria-hidden />} autoComplete="current-password" />
              </Form.Item>

              {/* The code field appears only once the API has told us this account uses MFA. */}
              {mfaRequired ? (
                <Form.Item
                  name="mfaCode"
                  label={t('auth.mfaCode')}
                  extra={t('auth.mfaRequired')}
                  rules={[{ required: true, message: t('common.required') }]}
                >
                  <Input
                    prefix={<ShieldCheck aria-hidden />}
                    inputMode="numeric"
                    maxLength={8}
                    autoComplete="one-time-code"
                    autoFocus
                  />
                </Form.Item>
              ) : null}

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={submitting}
                icon={<SubmitArrow size={20} />}
                iconPosition="end"
                className="bp-login__submit"
              >
                {t('auth.signIn')}
              </Button>
            </Form>

            {/* Barah Pay is a Barah product, and the corporate lockup is what says so. */}
            <div className="bp-login__signature">
              <BarahWordmark height={16} />
            </div>
          </div>
        </ConfigProvider>
      </main>
    </div>
  );
}
